import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';

import { renderMarkdown } from './markdown';

/*
 * Renders an assistant reply.
 *
 * The HTML comes from ./markdown.js, which escapes its input before adding any
 * markup — nothing the model wrote can introduce a tag here.
 *
 * Tables and ```csv blocks come back with a "Download CSV" button. The renderer
 * stays a pure string function, so the payloads travel alongside the HTML and
 * are wired up here by one delegated click handler rather than by markup that
 * carries the data. That also keeps the CSV out of the DOM — a large export is
 * never duplicated into an href.
 */
const Markdown = ({ text }) => {
  const containerRef = useRef(null);
  const { html, downloads } = useMemo(() => renderMarkdown(text), [text]);

  // Held on a ref so the delegated listener never reads a stale payload
  // between streaming re-renders.
  const downloadsRef = useRef(downloads);
  downloadsRef.current = downloads;

  const handleClick = useCallback((event) => {
    const trigger =
      event.target.closest && event.target.closest('[data-download]');
    if (!trigger) return;

    const payload = downloadsRef.current.find(
      (d) => d.id === trigger.getAttribute('data-download')
    );
    if (!payload) return;

    // Leading BOM so Excel reads it as UTF-8 — without it, accented creator
    // names arrive mangled, and those are exactly what these exports contain.
    const blob = new Blob([`﻿${payload.csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = payload.filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Revoking synchronously can cancel the save before the browser has read
    // the blob, so leave it a moment.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    node.addEventListener('click', handleClick);
    return () => node.removeEventListener('click', handleClick);
  }, [handleClick]);

  return (
    <Box
      ref={containerRef}
      className="ai-chat-markdown"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        fontSize: 15,
        lineHeight: 1.65,
        color: 'text.primary',
        wordBreak: 'break-word',
        '& p': { margin: '0 0 12px' },
        '& p:last-child': { marginBottom: 0 },
        '& h3, & h4, & h5, & h6': {
          margin: '20px 0 8px',
          fontWeight: 600,
          lineHeight: 1.3,
        },
        '& h3': { fontSize: 17 },
        '& h4': { fontSize: 15.5 },
        '& ul, & ol': { margin: '0 0 12px', paddingLeft: 22 },
        '& li': { margin: '3px 0' },
        '& a': { color: 'primary.main' },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.88em',
          background: 'rgba(0,0,0,0.06)',
          padding: '1px 5px',
          borderRadius: 4,
        },
        '& pre': {
          background: 'rgba(0,0,0,0.06)',
          padding: '12px 14px',
          borderRadius: 8,
          overflowX: 'auto',
          margin: '0 0 12px',
        },
        '& pre code': { background: 'none', padding: 0, fontSize: 13 },
        '& blockquote': {
          margin: '0 0 12px',
          padding: '2px 0 2px 12px',
          borderLeft: '3px solid rgba(0,0,0,0.15)',
          color: 'text.secondary',
        },
        '& hr': {
          border: 0,
          borderTop: '1px solid rgba(0,0,0,0.1)',
          margin: '16px 0',
        },

        // A result set presented as a file: header bar, then the table.
        '& .md-file': {
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 8,
          overflow: 'hidden',
          margin: '0 0 14px',
        },
        '& .md-dl': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '7px 10px 7px 12px',
          background: 'rgba(0,0,0,0.035)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        },
        '& .md-dl-meta': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12,
          color: 'text.secondary',
        },
        '& .md-dl-btn': {
          font: 'inherit',
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.4,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'primary.main',
          color: 'primary.main',
          background: 'transparent',
          borderRadius: 6,
          padding: '3px 10px',
          whiteSpace: 'nowrap',
        },
        '& .md-dl-btn:hover': { background: 'rgba(0,0,0,0.04)' },
        '& .md-dl-more': {
          padding: '6px 12px',
          fontSize: 12.5,
          color: 'text.secondary',
          borderTop: '1px solid rgba(0,0,0,0.08)',
        },

        // Wide comparison tables are the whole point of this feature; let them
        // scroll inside the bubble rather than stretch the page.
        '& .md-table-wrap': { overflowX: 'auto', margin: 0 },
        '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 14 },
        '& th, & td': {
          borderBottom: '1px solid rgba(0,0,0,0.09)',
          padding: '7px 10px',
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': {
          background: 'rgba(0,0,0,0.02)',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        },
        '& tbody tr:last-child td': { borderBottom: 'none' },
      }}
    />
  );
};

export default Markdown;
